import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForgotPassword } from '@/api/forgot-password';
import { useTranslation } from '@/contexts/i18n-context';
import { requiredString } from '@/util/validators';
import { useNavigate } from '@/util/react-router-dom';
import { UserEntryFormContainer } from '@/components/auth/user-entry-form-container';
import { UserEntryLayout } from '@/components/auth/user-entry-layout';
import { ForgotPasswordErrorType } from '@/api/api.schemas';
import { Banner } from '@/components/banner';
import { useBranding } from '@/outlets/branding-outlet';

const schema = z.object({
  username: requiredString(),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { networkId } = useBranding();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  const { mutate: forgotPassword, isPending, error } = useForgotPassword();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    forgotPassword(
      { data: { username: data.username, networkId } },
      {
        onSuccess: () => navigate(`/forgot-password/success`),
        onError: (error) => {
          if (error.type === ForgotPasswordErrorType.UserNotFound) {
            form.setError('username', { message: t('passwordReset.forgot.usernameError') });
          }
        },
      },
    );
  };

  return (
    <UserEntryLayout>
      <UserEntryFormContainer
        title={t('passwordReset.pageTitle')}
        description={t('passwordReset.forgot.description')}
        form={form}
        onSubmit={onSubmit}
        showSupportLink
      >
        {!!error &&
          error.type !== ForgotPasswordErrorType.UserNotFound &&
          (error.type === ForgotPasswordErrorType.MigrationFailed ? (
            <Banner type="error">
              <span>
                {t('passwordReset.forgot.error.migrationFailed', {
                  link: (
                    <Button
                      variant="link"
                      as="a"
                      to="/support"
                      size="S"
                      key="forgot-password-support-link"
                    >
                      {t('passwordReset.forgot.error.link')}
                    </Button>
                  ),
                })}
              </span>
            </Banner>
          ) : (
            <Banner type="error">{t('common.errors.unknown')}</Banner>
          ))}
        <TextInput
          name="username"
          label={t('passwordReset.forgot.label')}
          placeholder={t('passwordReset.forgot.placeholder')}
        />
        <Button fullWidth loading={isPending} type="submit">
          {t('passwordReset.forgot.submit')}
        </Button>
      </UserEntryFormContainer>
    </UserEntryLayout>
  );
}
